import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useQueryClient } from '@tanstack/react-query';
import { Node as ReactFlowNode } from 'reactflow';
import type { UrlFilters } from '../../../hooks/useUrlFilters';
import type { LineageGraphViewInput } from '../../../services/gql/types';
import { useLineageGraphContext } from '../LineageGraphContext';
import { MAX_GRAPH_NODES_WARNING } from '../constants';
import type {
  HopLimit,
  LineageDataResult,
  LineageGraphResponse,
  LineageGraphSource,
  ReactFlowGraph,
  ReactFlowNodeData,
} from '../types';
import { isColumnNode } from '../utils/lineageApiParsing';
import { getNodeTotals } from '../utils/lineageGraphPipeline';
import {
  getLineageGraphView,
  getLockedPositions,
  parseLineageGraphResponse,
  ParsedLineageGraph,
} from '../utils/lineageGraphResponse';
import {
  getReactFlowNodeChildrenRecursively,
  getReactFlowNodeParentIds,
  getVisibleLineageNodes,
  hideAndShiftGraphEdges,
  highlightNode,
  insertAssetColumns,
  toggleExpandedNode,
} from '../utils/lineageGraphUtils';
import { useFetchAssetColumns } from './useAssetColumns';
import useBaseLineageGraphQuery from './useBaseLineageGraphQuery';

type Props = {
  enabled: boolean;
  source?: LineageGraphSource;
  graphNodes?: ReactFlowNode<ReactFlowNodeData>[];
  assetIds?: string[];
  columnIds?: string[];
  filters?: UrlFilters;
  hopLimit?: HopLimit;
  forceLoadLargeGraph?: boolean;
};

type GraphState = ParsedLineageGraph & { response: LineageGraphResponse };

const hasLoadedColumnChildren = (node: ReactFlowNode<ReactFlowNodeData>) =>
  !!node.data.children?.some((child) => isColumnNode(child.data.metadata));

const withoutValue = <T>(set: Set<T>, value: T): Set<T> => {
  if (!set.has(value)) return set;
  const next = new Set(set);
  next.delete(value);
  return next;
};

const useLineageGraph = ({
  enabled,
  source,
  graphNodes,
  assetIds,
  columnIds,
  filters,
  hopLimit,
  forceLoadLargeGraph,
}: Props): LineageDataResult => {
  const queryClient = useQueryClient();
  const { highlightedNode, setHighlightedNode } = useLineageGraphContext();
  const { fetchAssetColumns } = useFetchAssetColumns();
  const [graphState, setGraphState] = useState<GraphState>();
  const [isRelayouting, setIsRelayouting] = useState(false);
  const [columnsLoadingAssetIds, setColumnsLoadingAssetIds] = useState<
    Set<string>
  >(new Set());

  const graphStateRef = useRef<GraphState>();
  graphStateRef.current = graphState;
  const loadedColumnsAssetIdsRef = useRef<string[]>([]);
  const baseVersionRef = useRef(0);
  const relayoutRequestIdRef = useRef(0);
  const reportedCycleKeyRef = useRef<string>();

  const selectedNodes = useMemo(
    () => [...(assetIds ?? []), ...(columnIds ?? [])],
    [assetIds, columnIds],
  );
  const hasColumnFilter = !!columnIds?.length;
  const hasActiveFiltersOrHops = (filters?.length ?? 0) > 0 || !!hopLimit;
  const selectionTooLarge =
    (assetIds?.length ?? 0) >= MAX_GRAPH_NODES_WARNING &&
    !forceLoadLargeGraph &&
    !hasActiveFiltersOrHops;
  const isGraphEnabled =
    enabled && !!source && selectedNodes.length > 0 && !selectionTooLarge;

  const baseQuery = useBaseLineageGraphQuery(source, {
    enabled: isGraphEnabled,
    forceLoadLargeGraph,
  });

  const currentGraph = useMemo(
    (): ReactFlowGraph => ({
      nodes: graphNodes ?? [],
      edges: graphState?.reactFlowGraph.edges ?? [],
    }),
    [graphNodes, graphState],
  );
  const currentGraphRef = useRef(currentGraph);
  currentGraphRef.current = currentGraph;

  const reportCycle = useCallback((cycleNodeIds: string[]) => {
    const cycleKey = cycleNodeIds.length
      ? [...cycleNodeIds].sort().join(',')
      : undefined;
    if (!cycleKey || cycleKey === reportedCycleKeyRef.current) return;
    reportedCycleKeyRef.current = cycleKey;
    const cycleError = new Error(
      `Lineage graph contains a cycle. Nodes on cycle: ${cycleNodeIds.join(
        ', ',
      )}`,
    );
    cycleError.name = 'LineageGraphCycle';
    datadogRum.addError(cycleError, {
      tags: ['lineage-cycle'],
      cycleNodes: cycleNodeIds,
      currentPath: window.location.pathname,
      queryParams: window.location.search,
    });
  }, []);

  const applyResponse = useCallback(
    (response: LineageGraphResponse, highlightedNodeId?: string) => {
      setGraphState({
        ...parseLineageGraphResponse(response, highlightedNodeId),
        response,
      });
      reportCycle(response.cycleNodeIds);
    },
    [reportCycle],
  );

  const baseQueryKey = JSON.stringify([source?.queryKey, forceLoadLargeGraph]);
  useEffect(() => {
    baseVersionRef.current += 1;
    loadedColumnsAssetIdsRef.current = [];
    setColumnsLoadingAssetIds(new Set());
    setIsRelayouting(false);
    if (enabled) {
      setHighlightedNode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQueryKey, enabled]);

  useEffect(() => {
    if (baseQuery.data) applyResponse(baseQuery.data);
  }, [baseQuery.data, applyResponse]);

  const requestGraph = useCallback(
    async (view: Partial<LineageGraphViewInput>) => {
      if (!source) return;
      const baseVersion = baseVersionRef.current;
      relayoutRequestIdRef.current += 1;
      const requestId = relayoutRequestIdRef.current;
      const fullView = getLineageGraphView({
        ...view,
        loadedColumnsAssetIds: loadedColumnsAssetIdsRef.current,
        forceLoadLargeGraph: !!forceLoadLargeGraph,
      });
      const isCurrentRequest = () =>
        baseVersion === baseVersionRef.current &&
        requestId === relayoutRequestIdRef.current;

      setIsRelayouting(true);
      try {
        const response = await queryClient.fetchQuery({
          queryKey: [...source.queryKey, fullView],
          queryFn: () => source.fetchGraph(fullView),
          staleTime: Infinity,
        });
        if (response && isCurrentRequest()) {
          applyResponse(response, fullView.highlightedNodeId ?? undefined);
        }
      } catch (error) {
        datadogRum.addError(error, { tags: ['lineage-graph-relayout'] });
      } finally {
        if (isCurrentRequest()) setIsRelayouting(false);
      }
    },
    [applyResponse, forceLoadLargeGraph, queryClient, source],
  );

  const updateReactFlowGraph = useCallback((reactFlowGraph: ReactFlowGraph) => {
    const state = graphStateRef.current;
    if (state) setGraphState({ ...state, reactFlowGraph });
  }, []);

  const cancelPendingRelayout = useCallback(() => {
    relayoutRequestIdRef.current += 1;
    setIsRelayouting(false);
  }, []);

  const onToggleExpandNode = useCallback(
    async (node: ReactFlowNode<ReactFlowNodeData>) => {
      const state = graphStateRef.current;
      if (!state) return;

      const children = getReactFlowNodeChildrenRecursively(node);
      if (children?.some((n) => n.id === highlightedNode?.id)) {
        setHighlightedNode(undefined);
        requestGraph({
          collapsedNodeIds: [node.id],
          lockedPositions: getLockedPositions(
            [node.id],
            currentGraphRef.current.nodes,
          ),
        });
        return;
      }

      const shouldExpand = !node.data.isExpanded;
      const needsColumnFetch =
        !hasColumnFilter &&
        shouldExpand &&
        !hasLoadedColumnChildren(node) &&
        (node.data.columnsCount ?? 0) > 0;

      if (needsColumnFetch) {
        const baseVersion = baseVersionRef.current;
        setColumnsLoadingAssetIds((prev) => new Set(prev).add(node.id));
        try {
          const columns = await fetchAssetColumns(node.id);
          const latestState = graphStateRef.current;
          if (baseVersion !== baseVersionRef.current || !latestState) return;

          loadedColumnsAssetIdsRef.current = [
            ...loadedColumnsAssetIdsRef.current.filter((id) => id !== node.id),
            node.id,
          ];
          const { highlightColumnIds, rootNodeIds } = latestState.response;
          updateReactFlowGraph(
            insertAssetColumns({
              workerData: latestState.workerData,
              reactFlowGraph: currentGraphRef.current,
              assetId: node.id,
              columns,
              expandedNodeIds: latestState.reactFlowGraph.nodes
                .filter((n) => n.data.isExpanded)
                .map((n) => n.id),
              rootNodeIds,
              selectedNodes,
              highlightedNodeId: highlightedNode?.id,
              highlightColumnIds: highlightColumnIds
                ? new Set(highlightColumnIds)
                : undefined,
              getChildNodeTotals: (childNode) =>
                getNodeTotals(childNode, filters),
            }),
          );
        } catch (error) {
          datadogRum.addError(error, { tags: ['lineage-asset-columns'] });
        } finally {
          setColumnsLoadingAssetIds((prev) => withoutValue(prev, node.id));
        }
        return;
      }

      updateReactFlowGraph(
        hideAndShiftGraphEdges(
          state.workerData,
          toggleExpandedNode(node, currentGraphRef.current),
          selectedNodes,
          highlightedNode?.id,
        ),
      );
    },
    [
      fetchAssetColumns,
      filters,
      hasColumnFilter,
      highlightedNode,
      requestGraph,
      selectedNodes,
      setHighlightedNode,
      updateReactFlowGraph,
    ],
  );

  const onToggleHighlightNode = useCallback(
    (node?: ReactFlowNode<ReactFlowNodeData>) => {
      const state = graphStateRef.current;
      if (!state) return;
      const stateNodes = state.reactFlowGraph.nodes;

      const prevHighlightedNode = highlightedNode
        ? stateNodes.find((n) => n.id === highlightedNode.id)
        : undefined;

      const nodeToSet =
        !node || node.id === highlightedNode?.id
          ? undefined
          : {
              id: node.id,
              parentId: node.data.metadata.parent,
              metadata: node.data.metadata,
            };

      if (!highlightedNode && !nodeToSet) return;

      setHighlightedNode(nodeToSet);

      const isChild = !!node?.data?.parentNode;

      if (!isChild && !prevHighlightedNode?.data.parentNode) {
        cancelPendingRelayout();
        updateReactFlowGraph(
          highlightNode(state.workerData, nodeToSet?.id, {
            ...currentGraphRef.current,
          }),
        );
        return;
      }

      const isColumnChild = isChild && isColumnNode(node?.data.metadata);
      if (!hasColumnFilter && isColumnChild && nodeToSet) {
        const highlightedReactFlowNode = stateNodes.find(
          (n) => n.id === nodeToSet.id,
        );
        const columnParentIds =
          highlightedReactFlowNode &&
          getReactFlowNodeParentIds(highlightedReactFlowNode);
        requestGraph({
          highlightedNodeId: nodeToSet.id,
          expandedNodeIds: columnParentIds,
          lockedPositions: getLockedPositions(
            columnParentIds,
            currentGraphRef.current.nodes,
          ),
        });
        return;
      }

      const prevHighlightedNodeParents =
        prevHighlightedNode && getReactFlowNodeParentIds(prevHighlightedNode);
      requestGraph({
        highlightedNodeId: nodeToSet?.id,
        expandedNodeIds: prevHighlightedNodeParents,
        lockedPositions: getLockedPositions(
          prevHighlightedNodeParents,
          currentGraphRef.current.nodes,
        ),
      });
    },
    [
      cancelPendingRelayout,
      hasColumnFilter,
      highlightedNode,
      requestGraph,
      setHighlightedNode,
      updateReactFlowGraph,
    ],
  );

  const activeState = isGraphEnabled ? graphState : undefined;
  const nodes = useMemo(
    () =>
      activeState?.response.exceedsSizeLimit
        ? []
        : getVisibleLineageNodes(
            activeState?.reactFlowGraph.nodes ?? [],
            columnsLoadingAssetIds,
          ),
    [activeState, columnsLoadingAssetIds],
  );
  const response = activeState?.response;

  return {
    nodes,
    edges: response?.exceedsSizeLimit
      ? []
      : activeState?.reactFlowGraph.edges ?? [],
    onToggleExpandNode,
    onToggleHighlightNode,
    dagDepthBoundary: response
      ? {
          incomingDepth: response.boundaries.upstream,
          outgoingDepth: response.boundaries.downstream,
        }
      : undefined,
    loading: (isGraphEnabled && baseQuery.isFetching) || isRelayouting,
    hitGraphLimits: selectionTooLarge || !!response?.exceedsSizeLimit,
    exceededLimitParentAssets: response?.exceededLimitParentAssets ?? [],
  };
};

export default useLineageGraph;
